/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Spin } from 'antd';
import { Box } from 'atoms';
import { API } from 'api';
import { ClientContractFormV2 } from 'componentsv2/forms';
import {
	searchClientFiltersV2,
	getClientContractByIdV2,
	searchRouteFiltersV2,
} from 'store/clientSliceV2';
import { searchVehicleTypesV2 } from 'store/vehicleSliceV2';

export default function CreateClientContractPageV2() {
	const dispatch = useDispatch();
	const [formData, setFormData] = useState({});
	const [loading, setLoading] = useState(false);
	const [clientsData, setClientsData] = useState([]);
	const [vehicleData, setVehicleData] = useState([]);
	const [filteredVehicleData, setFilteredVehicleData] = useState([]);
	const [projectData, setProjectData] = useState([]);
	const [routeData, setRouteData] = useState([]);
	const [loadingClients, setLoadingClients] = useState(false);
	const [loadingProjects, setLoadingProjects] = useState(false);
	const [loadingVehicleTypes, setLoadingVehicleTypes] = useState(false);
	const [loadingRoutes, setLoadingRoutes] = useState(false);
	const { id } = useParams();
	const location = useLocation();

	// Detect create mode vs view mode
	const isCreateMode = location.pathname.endsWith('/add');
	const [isViewMode, setIsViewMode] = useState(Boolean(id) && !isCreateMode);

	// Fetch dropdown data on mount
	useEffect(() => {
		const fetchDropdownData = async () => {
			// Only show loading if not in edit mode (edit mode has its own loading)
			if (!id) {
				setLoading(true);
			}
			try {
				// Fetch clients
				const clientsResponse = await dispatch(
					searchClientFiltersV2({ filter_by: { is_active: true }, limit: 1000 })
				);
				if (clientsResponse?.results) {
					setClientsData(
						clientsResponse.results.map((item) => ({
							value: item.value || item.id,
							label: item.label,
						}))
					);
				}

				// Fetch vehicle types using search API
				const vehicleTypes = await searchVehicleTypesAPI();
				setVehicleData(vehicleTypes);
			} catch (error) {
				console.error('Error fetching dropdown data:', error);
			} finally {
				if (!id) {
					setLoading(false);
				}
			}
		};

		fetchDropdownData();
	}, [dispatch, id]);

	// Helper function to load related data after fetching contract
	const loadContractRelatedData = async (data) => {
		// Fetch projects if we have client_id
		if (data.client_id) {
			setClientsData((prev) => {
				if (prev.some((c) => c.value === data.client_id)) return prev;
				return [{ value: data.client_id, label: data.client_name }, ...prev];
			});
		}
		if (data.project_id) {
			setProjectData([{ value: data.project_id, label: data.project_code }]);
		}

		if (data.client_id) {
			const projects = await searchProjectsAPI(null, data.client_id);
			if (
				data.project_id &&
				!projects.some((p) => String(p.value) === String(data.project_id))
			) {
				projects.unshift({ value: data.project_id, label: data.project_code });
			}
			setProjectData(projects);
		}

		// Fetch vehicle types if we have temperature_controlled
		if (data.temperature_controlled !== undefined && data.temperature_controlled !== null) {
			await handleVehicleTypeFetch(data.temperature_controlled);
		}

		// Fetch routes if we have project_id and vehicle_type_id
		if (data.project_id && data.vehicle_type_id) {
			await fetchRoutes(data.project_id, data.vehicle_type_id);
		}
	};

	// Fetch existing contract in edit mode
	useEffect(() => {
		if (!id) return;

		setLoading(true);
		// Get client_id from location state if available, otherwise use id
		const clientId = location.state?.clientId;
		dispatch(getClientContractByIdV2({ id, client_id: clientId }))
			.then(async (data) => {
				if (data) {
					setFormData(data);
					await loadContractRelatedData(data);
				}
			})
			.catch((error) => {
				console.error('Error fetching client contract:', error);
			})
			.finally(() => {
				setLoading(false);
			});
	}, [id, dispatch, location.state]);

	// Function to fetch routes based on project_id and vehicle_type_id
	const fetchRoutes = async (projectId, vehicleTypeId) => {
		if (!projectId || !vehicleTypeId) {
			setRouteData([]);
			return;
		}

		setLoadingRoutes(true);
		try {
			const routesResponse = await dispatch(
				searchRouteFiltersV2({
					filter_by: {
						project_id: Number(projectId),
						vehicle_type_id: Number(vehicleTypeId),
						is_active: true,
					},
					limit: 1000,
				})
			);

			if (routesResponse?.results) {
				setRouteData(
					routesResponse.results.map((item) => ({
						value: item.value || item.id,
						label: item.label || item.route_name,
					}))
				);
			} else {
				setRouteData([]);
			}
		} catch (error) {
			console.error('Error fetching routes:', error);
			setRouteData([]);
		} finally {
			setLoadingRoutes(false);
		}
	};

	// Function to search clients
	const handleSearchClients = async (searchTerm) => {
		if (!searchTerm || searchTerm.trim() === '') {
			// Load initial data if search is empty
			setLoadingClients(true);
			try {
				const response = await dispatch(
					searchClientFiltersV2({ filter_by: { is_active: true }, limit: 1000 })
				);
				if (response?.results) {
					setClientsData(
						response.results.map((item) => ({
							value: item.value || item.id,
							label: item.label,
						}))
					);
				}
			} catch (error) {
				console.error('Error fetching clients:', error);
			} finally {
				setLoadingClients(false);
			}
			return;
		}

		setLoadingClients(true);
		try {
			const response = await dispatch(
				searchClientFiltersV2({
					filter_by: { is_active: true, search_term: searchTerm },
					limit: 1000,
				})
			);
			if (response?.results) {
				setClientsData(
					response.results.map((item) => ({
						value: item.value || item.id,
						label: item.label,
					}))
				);
			}
		} catch (error) {
			console.error('Error searching clients:', error);
		} finally {
			setLoadingClients(false);
		}
	};

	// Helper: Clean filter_by object
	const cleanFilterBy = (filter_by) => {
		const cleaned = {};
		if (filter_by) {
			Object.keys(filter_by).forEach((key) => {
				const value = filter_by[key];
				if (value !== undefined && value !== null && value !== '') {
					cleaned[key] = value;
				}
			});
		}
		return cleaned;
	};

	// Helper: Map project response to dropdown format
	const mapProjectResponse = (response) => {
		if (!response?.data?.projects || !Array.isArray(response.data.projects)) {
			return [];
		}
		return response.data.projects.map((item) => ({
			value: item.id,
			label: item.project_code
				? `${item.project_code} - ${item.client_name || ''}`.trim()
				: item.project_name || `Project ${item.id}`,
		}));
	};

	// Function to search projects using search API
	const searchProjectsAPI = async (searchTerm = null, clientId = null) => {
		const filterBy = cleanFilterBy({
			is_active: true,
			...(searchTerm ? { query_str: searchTerm } : {}),
			...(clientId ? { client_id: clientId } : {}),
		});

		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'project',
				filter_by: filterBy,
				limit: 1000,
				offset: 0,
			},
			version: 'v2',
		};

		try {
			const response = await API.common(CREDENTIALS);
			return mapProjectResponse(response);
		} catch (error) {
			console.error('Error searching projects:', error);
			return [];
		}
	};

	// Function to search projects
	const handleSearchProjects = async (searchTerm, clientId = null) => {
		setLoadingProjects(true);
		try {
			const projects = await searchProjectsAPI(searchTerm, clientId);
			setProjectData(projects);
		} catch (error) {
			console.error('Error searching projects:', error);
			setProjectData([]);
		} finally {
			setLoadingProjects(false);
		}
	};

	// Helper: Map vehicle type response to dropdown format
	const mapVehicleTypeResponse = (response) => {
		if (!response?.data?.vehicle_types || !Array.isArray(response.data.vehicle_types)) {
			return [];
		}
		return response.data.vehicle_types.map((item) => ({
			value: item.id,
			label: item.vehicle_type_name || `Vehicle Type ${item.id}`,
			temperature_controlled: item.temperature_controlled || false,
			capacity_tonnes: item.capacity_tonnes || '',
		}));
	};

	// Function to search vehicle types using search API
	const searchVehicleTypesAPI = async (searchTerm = null) => {
		const filterBy = cleanFilterBy({
			is_active: true,
			...(searchTerm ? { vehicle_type_name: searchTerm } : {}),
		});

		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'vehicle_type',
				filter_by: filterBy,
				limit: 1000,
				offset: 0,
			},
			version: 'v2',
		};

		try {
			const response = await API.common(CREDENTIALS);
			return mapVehicleTypeResponse(response);
		} catch (error) {
			console.error('Error searching vehicle types:', error);
			return [];
		}
	};

	// Function to search vehicle types
	const handleSearchVehicleTypes = async (searchTerm) => {
		setLoadingVehicleTypes(true);
		try {
			const vehicleTypes = await searchVehicleTypesAPI(searchTerm);
			setVehicleData(vehicleTypes);
		} catch (error) {
			console.error('Error searching vehicle types:', error);
			setVehicleData([]);
		} finally {
			setLoadingVehicleTypes(false);
		}
	};

	// Function to fetch vehicle types based on temperature controlled
	const handleVehicleTypeFetch = async (temperatureControlled) => {
		if (temperatureControlled === undefined || temperatureControlled === null) {
			setFilteredVehicleData([]);
			return;
		}

		setLoadingVehicleTypes(true);
		try {
			// Convert temperature controlled value to boolean
			let temperatureControlledBool = false;
			if (typeof temperatureControlled === 'boolean') {
				temperatureControlledBool = temperatureControlled;
			} else if (typeof temperatureControlled === 'string') {
				temperatureControlledBool =
					temperatureControlled.toLowerCase() === 'yes' ||
					temperatureControlled === 'true';
			} else if (temperatureControlled === true || temperatureControlled === 1) {
				temperatureControlledBool = true;
			}

			const vehicleTypesResponse = await dispatch(
				searchVehicleTypesV2({
					filter_by: {
						temperature_controlled: temperatureControlledBool,
						is_active: true,
					},
					pagination: { page: 1, per_page: 1000 },
				})
			);

			const vehicleTypesData = vehicleTypesResponse?.rows || [];
			const vehicleTypes = vehicleTypesData.map((item) => ({
				value: item.id,
				label: item.vehicle_type_name || '-',
				temperature_controlled: item.temperature_controlled || false,
				capacity_tonnes: item.capacity_tonnes || '',
			}));

			setFilteredVehicleData(vehicleTypes);
		} catch (error) {
			console.error('Error fetching vehicle types:', error);
			setFilteredVehicleData([]);
		} finally {
			setLoadingVehicleTypes(false);
		}
	};

	if (loading) {
		return (
			<Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
				<Spin size="large" />
			</Box>
		);
	}

	return (
		<Box>
			<ClientContractFormV2
				isEditMode={Boolean(id)}
				isViewMode={isViewMode}
				setIsViewMode={setIsViewMode}
				formData={formData}
				clientsData={clientsData}
				vehicleData={filteredVehicleData.length > 0 ? filteredVehicleData : vehicleData}
				projectData={projectData}
				routeData={routeData}
				onFetchRoutes={fetchRoutes}
				onSearchClients={handleSearchClients}
				onSearchProjects={handleSearchProjects}
				onSearchVehicleTypes={handleSearchVehicleTypes}
				onVehicleTypeFetch={handleVehicleTypeFetch}
				loadingClients={loadingClients}
				loadingProjects={loadingProjects}
				loadingVehicleTypes={loadingVehicleTypes}
				loadingRoutes={loadingRoutes}
			/>
		</Box>
	);
}
